import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import UserTypePicker from "@/components/app/UserTypePicker";
import { Logo } from "@/components/app/Logo";
import { X } from "lucide-react";

const SESSION_KEY = "ydd_user_types_modal_dismissed";

/**
 * One-time post-signup nudge: appears for logged-in users whose `user_types`
 * is still empty. Dismissable for the session via X / "Skip for now" — they
 * can come back via the edit cards in Vet & Health or Settings.
 *
 * No segmentation of features happens off this answer; it's pure profile
 * data. Existing breeder UX still keys off `is_breeder`, which the backend
 * mirrors when "breeder" is in the saved list.
 */
export default function UserTypesModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    // Already answered → never show.
    if (Array.isArray(user.user_types) && user.user_types.length > 0) return;
    // Dismissed for this session → respect that.
    try {
      if (window.sessionStorage.getItem(SESSION_KEY)) return;
    } catch (err) { /* sessionStorage unavailable — show anyway */ }
    // Small delay so it doesn't clobber a fresh dashboard mount.
    const t = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch (err) { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
      data-testid="user-types-modal-backdrop"
      onClick={dismiss}
    >
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-card border-2 border-primary rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="user-types-modal"
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="user-types-modal-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Logo size={40} textClassName="text-base" />
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter leading-tight" data-testid="user-types-modal-headline">
            What brings you to Your Dog&nbsp;Days?
          </h2>
          <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
            Pick all that apply &mdash; you can change your answer anytime in Settings or your dog&apos;s Vet &amp; Health page.
          </p>

          <div className="mt-6">
            <UserTypePicker
              initial={[]}
              saveLabel="Save my profile"
              showSecondary
              secondaryLabel="Skip for now"
              onSecondary={dismiss}
              onSaved={() => setOpen(false)}
              data_testid="user-types-modal-picker"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
